import { useListProductOption } from '@bowtie-web/common';
import { uniq } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const useRidersSelection = ({
  productCodes,
  riderCodes,
  activeProduct,
  disable,
  multiple = true,
  defaultRiderOption,
}: {
  productCodes: string[];
  riderCodes?: 'default' | string;
  activeProduct?: string;
  disable?: boolean;
  multiple?: boolean;
  defaultRiderOption?: 'none' | string;
}) => {
  const [selectedRiders, setSelectedRiders] = useState<string[]>();

  // Read selectedRiders inside the effect without making it a dependency (to avoid
  // re-running the auto-select logic whenever the user manually changes their selection).
  const selectedRidersRef = useRef(selectedRiders);
  selectedRidersRef.current = selectedRiders;

  const [options] = useListProductOption(
    {
      type: 'rider',
      fromProducts: productCodes,
      // fetch all riders when riderCodes is not provided or in 'default' behavior
      ...(riderCodes &&
        riderCodes !== 'default' && { toProducts: riderCodes.split(',') }),
    },
    {
      // Also skip when activeProduct hasn't caught up to the new productCodes yet.
      // productCodes updates synchronously (useMemo) while activeProduct updates one
      // render later (useEffect in QuotationContext). Without this guard, the query
      // fires with the new productCodes but a stale activeProduct.
      skip:
        productCodes.length === 0 ||
        !activeProduct ||
        disable ||
        !productCodes.includes(activeProduct),
    },
  );

  /*
  Auto-select the default rider whenever the active product or available options
  change. 
  ┌─────────────────┬───────────────────────┬────────────────────┬──────────────────────────┬─────────────────────────────┐
  │ availableRiders │ currentSelectedRiders │     preserved      │    defaultRiderOption    │           Result            │
  ├─────────────────┼───────────────────────┼────────────────────┼──────────────────────────┼─────────────────────────────┤
  │ empty           │ any                   │ —                  │ any                      │ no-op (options not loaded   │
  │                 │                       │                    │                          │ yet)                        │
  ├─────────────────┼───────────────────────┼────────────────────┼──────────────────────────┼─────────────────────────────┤
  │ non-empty       │ any                   │ non-empty          │ any                      │ keep availableSelected      │
  ├─────────────────┼───────────────────────┼────────────────────┼──────────────────────────┼─────────────────────────────┤
  │ non-empty       │ [] (explicit)         │ empty              │ any                      │ no-op (preserve explicit    │
  │                 │                       │                    │                          │ deselect)                   │
  ├─────────────────┼───────────────────────┼────────────────────┼──────────────────────────┼─────────────────────────────┤
  │ non-empty       │ undefined (first      │ empty              │ 'none'                   │ []                          │
  │                 │ load)                 │                    │                          │                             │
  ├─────────────────┼───────────────────────┼────────────────────┼──────────────────────────┼─────────────────────────────┤
  │ non-empty       │ stale²                │ empty              │ 'none'                   │ []                          │
  ├─────────────────┼───────────────────────┼────────────────────┼──────────────────────────┼─────────────────────────────┤
  │ non-empty       │ undefined (first      │ empty              │ valid code in available  │ [defaultRiderOption]        │
  │                 │ load)                 │                    │ riders                   │                             │
  ├─────────────────┼───────────────────────┼────────────────────┼──────────────────────────┼─────────────────────────────┤
  │ non-empty       │ stale²                │ empty              │ valid code in available  │ [defaultRiderOption]        │
  │                 │                       │                    │ riders                   │                             │
  ├─────────────────┼───────────────────────┼────────────────────┼──────────────────────────┼─────────────────────────────┤
  │ non-empty       │ undefined (first      │ empty              │ unset / invalid code     │ [first rider]               │
  │                 │ load)                 │                    │                          │                             │
  ├─────────────────┼───────────────────────┼────────────────────┼──────────────────────────┼─────────────────────────────┤
  │ non-empty       │ stale²                │ empty              │ unset / invalid code     │ [first rider]               │
  └─────────────────┴───────────────────────┴────────────────────┴──────────────────────────┴─────────────────────────────┘

  ¹ preserved = intersection of currentSelectedRiders with the new product's rider codes (e.g. mock-common-rider-* survive a
  product switch; product-specific riders don't).
  ² "stale" = non-empty array that has no overlap with the new product's riders (all riders were product-specific, none carried over).
  */
  useEffect(() => {
    if (!activeProduct) return;

    const availableRiders = uniq(
      options
        .filter(opt => opt.from_product?.code === activeProduct)
        .map(({ to_product }) => to_product.code),
    );
    if (availableRiders.length === 0) return;

    const currentSelectedRiders = selectedRidersRef.current;
    const preserved = (currentSelectedRiders ?? []).filter(code =>
      availableRiders.includes(code),
    );

    if (preserved.length > 0) {
      setSelectedRiders(preserved);
    } else if (currentSelectedRiders?.length === 0) {
      // explicit empty deselect — preserve it across product switches
    } else if (defaultRiderOption === 'none') {
      setSelectedRiders([]);
    } else if (
      defaultRiderOption &&
      availableRiders.includes(defaultRiderOption)
    ) {
      setSelectedRiders([defaultRiderOption]);
    } else {
      setSelectedRiders(availableRiders.slice(0, 1));
    }
  }, [options, activeProduct, defaultRiderOption]);

  const productToRidersMapping = useMemo(
    () =>
      options.reduce(
        (prev: Record<string, string[]>, { from_product, to_product }) => {
          if (!from_product) {
            return prev;
          }
          const key = from_product.code;
          const prevValue = prev[key] || [];
          const value = [...prevValue, to_product.code];
          return { ...prev, [key]: Array.from(new Set(value)) };
        },
        {},
      ),
    [options],
  );

  const riders = useMemo(
    () => (activeProduct ? productToRidersMapping[activeProduct] : []) || [],
    [activeProduct, productToRidersMapping],
  );

  // There may be several rider options could be selected in future
  const onSelectedRiderChange = useCallback(
    (code: string) => {
      const index =
        selectedRiders?.findIndex(selected => selected === code) ?? -1;

      if (index === -1) {
        if (multiple) {
          setSelectedRiders(prev => [...(prev || []), code]);
        } else {
          setSelectedRiders([code]);
        }
        return;
      }

      setSelectedRiders(prev => prev?.filter((_, id) => id !== index));
    },
    [selectedRiders, multiple],
  );

  return {
    riders,
    selectedRiders: selectedRiders ?? [],
    onSelectedRiderChange,
    productToRidersMapping,
  };
};

export default useRidersSelection;
